import { Group, Menu, Portal, Text, Tooltip } from '@mantine/core';
import {
  IconBackspace,
  IconClipboard,
  IconCloudDownload,
  IconCooker,
  IconCopy,
  IconDotsVertical,
  IconFileImport,
  IconPencil,
  IconSettings,
  IconTrashX,
} from '@tabler/icons-react';
import { useApi } from 'api/api-context';
import ActionIcon from 'components/ActionIcon';
import Button from 'components/Button';
import panic from 'errors/panic';
import { noop } from 'lodash';
import { useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import { ERROR_NOTIFICATION_COLOR } from 'utils/constants';
import { useConfirm } from 'components/modals/message/MessageProvider';
import { useResponsiveDesign } from 'hooks/use-responsive-design';
import {
  DEVICES_PAGE_PATH,
  EDIT_REMOTE_SOURCE_PAGE_PATH,
  REVISIONS_PAGE_PATH,
  CONFIG_REMOTE_SOURCE_PAGE_PATH,
  COPY_REMOTE_SOURCE_PAGE_PATH,
} from 'routes/paths';
import { useSyncRemoteDeviceModal } from 'components/modals/sync-remote-device-modal/SyncRemoteDeviceProvider';
import { useImportXlsxModal } from 'components/modals/import-xlsx-modal/ImportXlsxModalProvider';
import { RemoteSourceConfig, XlsxConfig } from 'components/forms/remote-source/config/types';
import { RemoteSourceActionsProps } from 'components/tables/data/remote-source/types';

/**
 * The actions which can be performed on a single remote source row.
 */
export default function RemoteSourceActions({
  remoteSourceId,
  type,
  name,
  status,
  config,
  compactOnTablet = false,
  onStatusChange = noop,
}: RemoteSourceActionsProps) {
  const { getAction } = useApi();
  const { confirm } = useConfirm();
  const { isMobile, tableActionMode } = useResponsiveDesign();
  const isCompact = compactOnTablet && tableActionMode !== 'desktop';
  const menuOnly = compactOnTablet && tableActionMode === 'mobile';
  const { openSyncRemoteDeviceModal } = useSyncRemoteDeviceModal();
  const { openImportXlsxModal } = useImportXlsxModal();

  const configPath = useMemo(() => CONFIG_REMOTE_SOURCE_PAGE_PATH.insert({ remoteSourceId }), [remoteSourceId]);
  const editPath = useMemo(() => EDIT_REMOTE_SOURCE_PAGE_PATH.insert({ remoteSourceId }), [remoteSourceId]);
  const copyPath = useMemo(() => COPY_REMOTE_SOURCE_PAGE_PATH.insert({ remoteSourceId }), [remoteSourceId]);
  const devicesPath = useMemo(() => `${DEVICES_PAGE_PATH.original}?remoteSourceId=${remoteSourceId}`, [remoteSourceId]);
  const revisionsPath = useMemo(
    () => `${REVISIONS_PAGE_PATH.original}?remoteSourceId=${remoteSourceId}`,
    [remoteSourceId]
  );

  const parsedConfig = useMemo<RemoteSourceConfig | undefined>(() => {
    if (!config) {
      return undefined;
    }

    try {
      return JSON.parse(config) as RemoteSourceConfig;
    } catch (e) {
      return undefined;
    }
  }, [config, type]);

  const primaryAction = useMemo(() => {
    if (type === 'chastia') {
      return {
        label: 'Synchronizovať',
        icon: <IconCloudDownload />,
        onClick: () => openSyncRemoteDeviceModal({ remoteSourceId }),
      };
    }

    if (type === 'xlsx' && parsedConfig) {
      return {
        label: 'Importovať dáta',
        icon: <IconFileImport />,
        onClick: () => openImportXlsxModal({ config: parsedConfig as XlsxConfig, remoteSourceId }),
      };
    }

    return null;
  }, [type, parsedConfig, openImportXlsxModal, openSyncRemoteDeviceModal, remoteSourceId]);

  /**
   * Makes a call to the API to set the status of the remote source.
   */
  const setStatusRemote = useCallback(
    (status: boolean) => {
      const remoteSourceSetStatus = getAction('RemoteSourceSetStatus');

      remoteSourceSetStatus({ parameters: { remoteSourceId: String(remoteSourceId) }, payload: { status } })
        .then(() => onStatusChange(status))
        .catch((e) => {
          notifications.show({
            title: 'Chyba!',
            message: 'Nepodarilo sa zmeniť stav externého zdroja.',
            color: ERROR_NOTIFICATION_COLOR,
          });
          panic(e);
        });
    },
    [getAction, remoteSourceId, onStatusChange]
  );

  /**
   * Confirms the status change.
   */
  const confirmSetStatus = useCallback(
    (status: boolean) => {
      const title = status ? 'Aktivovať externý zdroj' : 'Deaktivovať externý zdroj';
      const message = status
        ? 'Naozaj chcete aktivovať tento externý zdroj?'
        : 'Naozaj chcete deaktivovať tento externý zdroj?';

      confirm({
        title,
        content: message,
        onConfirm: () => setStatusRemote(status),
      });
    },
    [confirm, setStatusRemote]
  );

  return (
    <Group spacing={12} noWrap>
      {!menuOnly &&
        (isCompact ? (
          primaryAction ? (
            <Tooltip label={primaryAction.label} events={{ hover: true, focus: true, touch: true }}>
              <ActionIcon variant="secondary" size="md" disabled={!status} onClick={primaryAction.onClick}>
                {primaryAction.icon}
              </ActionIcon>
            </Tooltip>
          ) : (
            <Tooltip label="Konfigurácia" events={{ hover: true, focus: true, touch: true }}>
              <ActionIcon variant="secondary" size="md" component={Link} to={configPath}>
                <IconSettings stroke="1.5" />
              </ActionIcon>
            </Tooltip>
          )
        ) : primaryAction ? (
          <Button
            size="md"
            variant="secondary"
            disabled={!status}
            leftIcon={primaryAction.icon}
            onClick={primaryAction.onClick}
          >
            {primaryAction.label}
          </Button>
        ) : (
          // Config button
          <Button
            size="md"
            variant="secondary"
            component={Link}
            to={configPath}
            leftIcon={<IconSettings stroke="1.5" />}
          >
            Konfigurácia
          </Button>
        ))}
      <Menu position="bottom-end">
        <Menu.Target>
          <ActionIcon variant="tertiary" size={isMobile ? 'sm' : 'md'}>
            <IconDotsVertical stroke="1.5" />
          </ActionIcon>
        </Menu.Target>
        <Portal>
          <Menu.Dropdown>
            <Menu.Label>
              <Text maw={160} truncate>
                {name}
              </Text>
            </Menu.Label>
            {status && (
              <>
                {menuOnly && primaryAction && (
                  <Menu.Item icon={primaryAction.icon} onClick={primaryAction.onClick} disabled={!status}>
                    {primaryAction.label}
                  </Menu.Item>
                )}
                <Menu.Item component={Link} to={configPath} icon={<IconSettings stroke="1.5" />}>
                  Konfigurácia
                </Menu.Item>
                <Menu.Item component={Link} to={editPath} icon={<IconPencil stroke="1.5" />}>
                  Upraviť parametre
                </Menu.Item>
                <Menu.Item component={Link} to={copyPath} icon={<IconCopy stroke="1.5" size={20} />}>
                  Kopírovať
                </Menu.Item>
                <Menu.Divider />
                <Menu.Item component={Link} to={devicesPath} icon={<IconCooker stroke="1.5" />} color="gray.8">
                  Zobraziť zariadenia
                </Menu.Item>
                {type === 'chastia' && (
                  <Menu.Item component={Link} to={revisionsPath} icon={<IconClipboard stroke="1.5" />} color="gray.8">
                    Zobraziť revízie
                  </Menu.Item>
                )}
                <Menu.Divider />
              </>
            )}
            <Menu.Item
              color={status ? 'red.8' : 'red.8'}
              onClick={() => confirmSetStatus(!status)}
              icon={status ? <IconTrashX stroke={1.5} size={20} /> : <IconBackspace stroke={1.5} size={20} />}
            >
              {status ? 'Deaktivovať' : 'Aktivovať'}
            </Menu.Item>
          </Menu.Dropdown>
        </Portal>
      </Menu>
    </Group>
  );
}
